import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { Box, Flex, Paper, PaperProps, Stack, Text } from '@mantine/core';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRef } from 'react';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import buildTheme from './config/buildTheme';
import nodes from './config/nodes';
import DeprecatedMarkdownEditor from './DeprecatedMarkdownEditor';
import './markdown-editor.css';
import DragDropPaste from './plugins/DragDropPastePlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import ImagesPlugin from './plugins/ImagesPlugin';
import { MarkdownPlugin } from './plugins/MarkdownPlugin';
import { MarkdownShortcutPluginWrapper } from './plugins/MarkdownShortcutPluginWrapper';
import { OnChangePlugin } from './plugins/OnChangePlugin';
import PasteHandlerPlugin from './plugins/PasteHandlerPlugin';
import { SuggestionPlugin } from './plugins/SuggestionPlugin';
import { GroupedSuggestions, SuggestionTrackerPlugin } from './plugins/SuggestionTrackerPlugin';
import { SiestaInlinePlugin } from './plugins/SiestaInlinePlugin';
import TableActionMenuPlugin from './plugins/TableActionMenuPlugin';
import TableHoverActionsPlugin from './plugins/TableHoverActionsPlugin';

type EditorProps = {
  defaultValue?: string;
  onChange?: (value: string) => void;
  label?: string | React.ReactNode;
  headerRightComponent?: React.ReactNode;
  readonly?: boolean;
  placeholderImages?: MarkdownImage[];
  rootClass?: 'default' | 'compact';
  onSuggestionsChange?: (suggestions: GroupedSuggestions) => void;
  bare?: boolean;
  showMarkers?: boolean;
  enableInlineEditing?: boolean;
} & PaperProps;

export default function MarkdownEditor(props: EditorProps) {
  const { flagEnabledLexicalEditor } = useFlags();
  if (!flagEnabledLexicalEditor) {
    return <DeprecatedMarkdownEditor {...props} />;
  }
  return <NewMarkdownEditor {...props} />;
}

export function NewMarkdownEditor(props: EditorProps) {
  const {
    defaultValue,
    onChange = () => {},
    label,
    headerRightComponent,
    readonly = false,
    rootClass = 'default',
    onSuggestionsChange,
    bare,
    showMarkers = true,
    enableInlineEditing = false,
    ...paperProps
  } = props;

  if (label) {
    return (
      <Stack gap="xs" h="100%">
        {label && (
          <Flex align="center" justify="space-between">
            {typeof label === 'string' ? (
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.8">
                {label}
              </Text>
            ) : (
              label
            )}
            {headerRightComponent}
          </Flex>
        )}
        <Editor
          {...paperProps}
          defaultValue={defaultValue}
          onChange={onChange}
          readonly={readonly}
          rootClass={rootClass}
          onSuggestionsChange={onSuggestionsChange}
          bare={bare}
          showMarkers={showMarkers}
          enableInlineEditing={enableInlineEditing}
        />
      </Stack>
    );
  }

  return (
    <Editor
      {...paperProps}
      defaultValue={defaultValue}
      onChange={onChange}
      readonly={readonly}
      rootClass={rootClass}
      onSuggestionsChange={onSuggestionsChange}
      bare={bare}
      showMarkers={showMarkers}
      enableInlineEditing={enableInlineEditing}
    />
  );
}

function Editor({
  defaultValue,
  onChange,
  placeholderImages = [],
  readonly = false,
  rootClass,
  onSuggestionsChange,
  bare,
  showMarkers = true,
  enableInlineEditing = false,
  ...props
}: Omit<EditorProps, 'label' | 'headerRightComponent'>) {
  const initialConfig: InitialConfigType = {
    namespace: 'Editor',
    theme: buildTheme(rootClass),
    onError: (error, editor) => {
      // eslint-disable-next-line no-console
      console.error('MakrdownEditor error', error, editor);
      Sentry.captureException(error);
    },
    editable: !readonly,
    nodes,
  };

  const editorRef = useRef<HTMLDivElement>(null);

  const Container = bare ? Box : Paper;
  const containerProps = bare
    ? {
        ref: editorRef,
        style: {
          position: 'relative' as const,
          minHeight: '100px',
          overflowY: 'auto' as const,
        },
        bg: readonly ? 'gray.1' : undefined,
        ...props,
      }
    : {
        ref: editorRef,
        variant: 'content' as const,
        style: {
          position: 'relative' as const,
          minHeight: '100px',
          overflowY: 'auto' as const,
        },
        bg: readonly ? 'gray.1' : undefined,
        ...props,
      };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Container {...containerProps}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={{
                outline: 'none',
                padding: '12px',
                minHeight: '100px',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 1.5,
              }}
            />
          }
          placeholder={
            <div
              style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                color: 'var(--mantine-color-gray-5)',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              Start typing...
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <MarkdownPlugin defaultValue={defaultValue} placeholderImages={placeholderImages} />
        <OnChangePlugin onChange={onChange} placeholderImages={placeholderImages} />
        <SuggestionPlugin showMarkers={showMarkers} />
        {onSuggestionsChange && (
          <SuggestionTrackerPlugin onSuggestionsChange={onSuggestionsChange} />
        )}
        {enableInlineEditing && <SiestaInlinePlugin />}
        <HistoryPlugin />
        <AutoFocusPlugin />
        <MarkdownShortcutPluginWrapper />
        <PasteHandlerPlugin />
        <ImagesPlugin />
        <DragDropPaste />
        <LinkPlugin />
        <ClickableLinkPlugin />
        <TablePlugin />
        <ListPlugin />
        {!readonly && editorRef.current && (
          <>
            <FloatingTextFormatToolbarPlugin
              anchorElem={editorRef.current}
              enableInlineEditing={enableInlineEditing}
            />
            <TableHoverActionsPlugin anchorElem={editorRef.current} />
            <TableActionMenuPlugin anchorElem={editorRef.current} />
          </>
        )}
      </Container>
    </LexicalComposer>
  );
}
